import React from "react";
import photoUrl from "../assets/img/Training.JPEG";

const Training = () => {
  const trainingDays = [
    { day: 1, title: "Theory & Fundamentals", subtitle: "Building your foundation", gradient: "linear-gradient(135deg, #b0c4b1, #e3d5ca)",
      items: ["Color theory and pigment selection", "Skin anatomy, types, and challenges", "Machine & needle configurations", "Safety protocols & consultation"] },
    { day: 2, title: "Practical Training", subtitle: "Hands-on skill development", gradient: "linear-gradient(135deg, #e3d5ca, #ffe5ec)",
      items: ["Technique application & control", "Depth & pressure exercises", "Shading & blending techniques"] },
    { day: 3, title: "Live Model Practice", subtitle: "Real-world application", gradient: "linear-gradient(135deg, #ffe5ec, #c9ada7)",
      items: ["Supervised real model practice (up to 2 models per technique)", "Instructor guidance & corrections", "Aftercare & healing management"] },
  ];

  const techniques = [
    { title: "Brow Techniques", subtitle: "Shape & define", gradient: "linear-gradient(135deg, #b0c4b1, #e3d5ca)", 
      items: ["Microblading", "Ombre Brows", "Nano Brows", "Combo Brows"] },
    { title: "Lip Techniques", subtitle: "Enhance & perfect", gradient: "linear-gradient(135deg, #e3d5ca, #ffe5ec)", 
      items: ["Lip Blush", "Full Lip Contour", "Dark Lip Correction"] },
    { title: "Eyeliner Techniques", subtitle: "Define & accentuate", gradient: "linear-gradient(135deg, #ffe5ec, #c9ada7)", 
      items: ["Lash Line", "Winged Eyeliner", "Classic Eyeliner"] },
    { title: "Advanced Options", subtitle: "Specialized techniques", gradient: "linear-gradient(135deg, #c9ada7, #e0afa0)", 
      items: ["Scalp Micropigmentation (SMP)"], bold: true },
  ];

  const pricingCourses = [
    { title: "Brow Tattoo Course", subtitle: "Master 4 Advanced Techniques", borderColor: "border-primary", gradient: "from-primary to-secondary",
      bgGradient: "from-white via-primary/5 to-secondary/10", dotColor: "bg-primary",
      techniques: ["Microblading", "Ombre Brows", "Nano Brows", "Combo Brows"],
      pricing: [{ option: "1 Technique", price: "$1,990" }, { option: "2 Techniques", price: "$3,500" }, { option: "3 Techniques", price: "$4,990" },
        { option: "4 Techniques", price: "$5,990", featured: true, badge: "Full Package - Best Value!" }] },
    { title: "Lips Tattoo Course", subtitle: "Perfect Lip Artistry", borderColor: "border-secondary", gradient: "from-secondary to-primary",
      bgGradient: "from-white via-secondary/10 to-primary/5", dotColor: "bg-secondary",
      techniques: ["Full Lips Contour", "Lips Blush", "Dark Lips Correction"],
      pricing: [{ option: "1 Technique", price: "$1,990" }, { option: "3 Techniques", price: "$3,500", featured: true, badge: "Full Package - Complete Mastery!" }] },
    { title: "Eyes Tattoo Course", subtitle: "Professional Eyeliner Techniques", borderColor: "border-primary", gradient: "from-primary to-secondary",
      bgGradient: "from-white via-primary/5 to-secondary/10", dotColor: "bg-primary",
      techniques: ["Lash Enhancement", "Winged Eyeliner", "Classic Eyeliner"],
      pricing: [{ option: "1 Technique", price: "$1,990" }, { option: "3 Techniques", price: "$2,990", featured: true, badge: "Full Package - Best Value!" }] },
    { title: "SMP Course", subtitle: "Scalp Micropigmentation Mastery", borderColor: "border-secondary", gradient: "from-secondary to-primary",
      bgGradient: "from-white via-secondary/10 to-primary/5", dotColor: "bg-secondary",
      techniques: ["Complete SMP Techniques", "Hair Loss Solutions", "Density & Hairline Work"],
      pricing: [{ price: "$2,990", singlePrice: true, label: "Complete Training Package" }] },
  ];

  const features = [
    { title: "1:1 Private Training", desc: "Expert supervision with personalized attention ensuring you master every technique." },
    { title: "Hyper-Realistic Results", desc: "Focus on creating ultra-natural results that enhance beauty seamlessly." },
    { title: "Flexible Scheduling", desc: "Train at your pace with ongoing mentorship and post-training support." },
    { title: "All Skill Levels", desc: "Perfect for beginners and professionals seeking to refine their skills." },
    { title: "Certificate & Support", desc: "Official certification with continued mentorship for your success." },
  ];

  const includedItems = ["Complete training materials & notes", "Hands-on practice with live models", "Certificate of completion",
    "One-on-one personalized instruction", "Post-training mentorship support", "Professional 3-day training structure"];

  const CheckIcon = () => (
    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );

  const courseIncludes = ["Full training notes", "Certificate of Completion", "Personalized mentorship & post-training support"];

  return (
    <>
      <h1 className="md:text-5xl text-3xl justify-center flex my-8 m-auto p-2">Professional Training Programs</h1>

      <div className="flex flex-col-reverse md:mx-12 my-8 lg:flex-row flex-wrap items-start">
        <div className="w-full lg:w-3/5 pr-4 my-8 lg:mb-0">
          <div className="mb-6 p-8">
            <h2 className="md:text-3xl text-xl font-semibold mb-8 text-black">Sepiyar Aesthetics 1:1 Training Programs</h2>
            <p className="text-base leading-loose text-gray-800 lora-font mb-6">
              At Sepiyar Aesthetics, we offer exclusive private one-on-one cosmetic tattoo training designed to help you master every
              technique with confidence and precision. Our programs combine in-depth theory, hands-on practice, and live model experience —
              ensuring you graduate fully prepared to start your professional career.
            </p>

            <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6 rounded-lg border-l-4 border-primary mb-8">
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Each course includes:</h3>
              <ul className="space-y-3 text-gray-700">
                {courseIncludes.map((item, idx) => (
                  <li key={idx} className="flex items-center">
                    <div className="w-6 h-6 bg-gradient-to-r from-primary to-primary/80 rounded-full flex items-center justify-center mr-3 shadow-sm">
                      <CheckIcon />
                    </div>
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="w-full lg:w-2/5 p-4 md:p-0 md:pl-4 md:my-8 lg:mb-0">
          <img src={photoUrl} alt="Professional Training Equipment" className="rounded-lg shadow-lg w-[600px] h-[400px] object-cover mx-auto lg:mx-0" />
        </div>
      </div>

      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl text-gray-800 mb-4">Training Overview</h2>
            <p className="text-xl text-gray-600">Professional 3-Day Structure</p>
            <div className="w-24 h-1 bg-primary mx-auto mt-4"></div>
          </div>

          <div className="max-w-5xl mx-auto">
            <p className="text-center text-lg text-gray-700 mb-12">
              All cosmetic tattoo programs (Brows, Lips, Eyeliner, and Scalp Micropigmentation) follow our professional 3-day structure:
            </p>

            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary/30 via-primary/60 to-primary/30 rounded-full hidden md:block"></div>
              
              {trainingDays.map((day, index) => (
                <div key={day.day} className="relative mb-12 md:mb-8">
                  <div className="flex flex-col md:flex-row items-center">
                    <div className={`w-full ${index % 2 === 0 ? 'md:w-5/12 md:pr-8' : 'md:w-7/12'} mb-4 md:mb-0`}>
                      {index % 2 === 0 && (
                        <div className={`bg-white rounded-3xl shadow-xl overflow-hidden transform hover:scale-105 ${index % 2 === 0 ? 'hover:rotate-1' : 'hover:-rotate-1'} transition-all duration-500 border-t-4 border-primary`}>
                          <div style={{ background: day.gradient }} className="p-6 relative">
                            <div className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                              <span className="text-primary font-bold text-sm">{day.day}</span>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">{day.title}</h3>
                            <p className="text-gray-600 text-sm">{day.subtitle}</p>
                          </div>
                          <div className="p-6 bg-gradient-to-br from-gray-50 to-white">
                            <div className="grid grid-cols-1 gap-3">
                              {day.items.map((item, i) => (
                                <div key={i} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-primary/5 transition-colors">
                                  <div className="w-3 h-3 bg-gradient-to-r from-primary to-primary/60 rounded-full"></div>
                                  <p className="text-gray-700 text-sm font-medium">{item}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="hidden md:flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary to-primary/80 rounded-full shadow-xl border-4 border-white z-10">
                      <span className="text-white font-bold text-lg">{day.day}</span>
                    </div>

                    <div className={`w-full ${index % 2 === 1 ? 'md:w-5/12 md:pl-4 flex justify-end' : 'md:w-5/12 md:pl-8'}`}>
                      {index % 2 === 1 && (
                        <div className="bg-white rounded-3xl shadow-xl overflow-hidden transform hover:scale-105 hover:-rotate-1 transition-all duration-500 border-t-4 border-primary w-full">
                          <div style={{ background: day.gradient }} className="p-6 relative">
                            <div className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                              <span className="text-primary font-bold text-sm">{day.day}</span>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">{day.title}</h3>
                            <p className="text-gray-600 text-sm">{day.subtitle}</p>
                          </div>
                          <div className="p-6 bg-gradient-to-br from-gray-50 to-white">
                            <div className="grid grid-cols-1 gap-3">
                              {day.items.map((item, i) => (
                                <div key={i} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-primary/5 transition-colors">
                                  <div className="w-3 h-3 bg-gradient-to-r from-primary to-primary/60 rounded-full"></div>
                                  <p className="text-gray-700 text-sm font-medium">{item}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Techniques Section */}
      <div className="bg-gradient-to-br from-gray-50 via-white to-gray-100 py-16">
        <div className="container mx-auto px-8">
          <div className="text-center mb-16">
            <div className="inline-block">
              <h2 className="text-5xl text-gray-800 mb-4 relative">Techniques Covered
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-primary via-primary/60 to-primary rounded-full"></div>
              </h2>
            </div>
            <p className="text-xl text-gray-600 mt-6">Master All Major Cosmetic Tattoo Techniques</p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 p-6 bg-white rounded-2xl shadow-lg border-l-4 border-primary">
              <p className="text-lg text-gray-700 font-medium">
                Our 1:1 courses cover all major cosmetic tattoo and paramedical techniques, which can be taken individually or as part of a complete package
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {techniques.map((tech, index) => (
                <div key={index} className={`group bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 ${index % 2 === 0 ? 'hover:rotate-1' : 'hover:-rotate-1'} transition-all duration-500 border-t-4 border-primary`}>
                  <div style={{ background: tech.gradient }} className="p-6 relative">
                    <h4 className="text-xl font-bold text-gray-800 mb-2">{tech.title}</h4>
                    <p className="text-gray-600 text-sm">{tech.subtitle}</p>
                  </div>
                  <div className="p-6 bg-gradient-to-br from-gray-50 to-white">
                    <ul className="space-y-3 text-gray-700">
                      {tech.items.map((item, i) => (
                        <li key={i} className="flex items-center space-x-3">
                          <div className="w-2 h-2 bg-gradient-to-r from-primary to-primary/60 rounded-full"></div>
                          <span className={`text-sm ${tech.bold ? 'font-bold' : 'font-medium'}`}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 p-8 rounded-2xl shadow-lg border border-primary/20">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary to-primary/80 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Complete Training Package</h3>
                <p className="text-lg text-gray-700 font-medium">
                  All prices include theory sessions, latex practice, live model training, full training notes, and an official Certificate of Completion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Us & Pricing */}
      <div className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="container mx-auto px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-800 mb-6">Why Choose Sepiyar Aesthetics</h2>
            <div className="w-32 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Excellence in Every Detail - Your Success is Our Priority</p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {features.map((feature, index) => (
                <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-primary">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pricing Section */}
            <div className="mt-20 mb-16">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">Course Pricing</h2>
                <p className="text-lg text-gray-600">Choose the perfect training package for your career goals</p>
                <div className="w-24 h-1 bg-primary mx-auto mt-4"></div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                {pricingCourses.map((course, index) => (
                  <div key={index} className={`bg-gradient-to-br ${course.bgGradient} rounded-3xl shadow-2xl overflow-hidden border-t-4 ${course.borderColor} transform hover:scale-105 transition-all duration-300`}>
                    <div className={`bg-gradient-to-r ${course.gradient} p-4`}>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{course.title}</h3>
                      <p className="text-gray-700 text-sm">{course.subtitle}</p>
                    </div>
                    
                    <div className="p-5">
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-800 mb-3 text-base">{course.pricing[0].singlePrice ? "What You'll Learn:" : "Techniques Covered:"}</h4>
                        <ul className="space-y-2">
                          {course.techniques.map((tech, i) => (
                            <li key={i} className="flex items-center text-gray-700 text-sm">
                              <div className={`w-2 h-2 ${course.dotColor} rounded-full mr-3`}></div>
                              <span>{tech}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="border-t border-gray-200 pt-4">
                        {course.pricing[0].singlePrice ? (
                          <div className="text-center">
                            <p className="text-gray-600 mb-3 text-sm">{course.pricing[0].label}</p>
                            <div className="inline-block">
                              <div className={`bg-gradient-to-r ${course.gradient.includes('secondary') ? 'from-secondary/20 to-primary/20' : 'from-primary/20 to-secondary/20'} rounded-2xl p-4 border-2 ${course.borderColor} shadow-lg`}>
                                <div className="text-4xl font-bold text-gray-900 mb-1">{course.pricing[0].price}</div>
                                <p className="text-xs text-gray-600">Full Comprehensive Training</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <h4 className="font-semibold text-gray-800 mb-3 text-base">Pricing Options:</h4>
                            <div className="space-y-2">
                              {course.pricing.map((price, i) => (
                                <div key={i} className={`flex justify-between items-center ${price.featured ? 'p-3 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg shadow-md border-2 border-primary' : 'p-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow'}`}>
                                  {price.featured ? (
                                    <>
                                      <div>
                                        <span className="text-gray-900 font-semibold text-sm">{price.option}</span>
                                        <span className="block text-xs text-gray-600">{price.badge}</span>
                                      </div>
                                      <span className="font-bold text-gray-900 text-lg">{price.price}</span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-gray-700 text-sm">{price.option}</span>
                                      <span className="font-bold text-gray-900">{price.price}</span>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <div className="bg-white rounded-3xl shadow-xl p-10 border-t-4 border-primary">
                <div className="max-w-3xl mx-auto">
                  <h3 className="text-3xl font-bold text-gray-800 mb-4">Ready to Start Your Journey?</h3>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Transform your passion into a thriving career with comprehensive training that sets you apart in the competitive beauty industry.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <a href="/ContactUs" className="bg-primary text-black px-8 py-4 rounded-xl font-semibold hover:bg-primary/90 transition-colors duration-300 flex items-center space-x-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      <span>Contact Us Today</span>
                    </a>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">Available Now</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Training;
